<template>
  <div>
    <div
      class="rendered-text-container fs-block"
      v-show="!getEditMode"
      @click="toggleEditIfNoSelection()"
      @keyup.enter="toggleEditIfNoSelection()"
      @copy="handleKeyboardCopy"
      tabindex="0">
      <WikiText
        v-if="value"
        :text="value"
        :strategies="strategies"
        :resources="resources"
      />
      <i v-else-if="placeholderText" class="text-muted">{{ placeholderText }}</i>
      <div class="rendered-text-container__hover gb-text-1">
        <small>Click anywhere to begin editing!</small>
      </div>
    </div>
    <label :for="textareaId" class="sr-only">Editable text</label>
    <WikiTextArea
      class="fs-block"
      :text="value"
      :textarea-id="textareaId"
      v-if="getEditMode"
      @editedText="currentEditedText = $event"
      @blurred="updateValue"
    />
    <div class="flex-container">
      <div class="btn-toolbar" v-if="showButtons">
        <button
          class="btn btn-primary btn-xs"
          @click="toggleEditMode(true)"
          v-show="!getEditMode"
          :aria-label="contentTitle ? `Edit ${contentTitle} statement` : 'Edit'"><i class="fa fa-edit"/> Edit</button>
        <button
          class="btn btn-success btn-xs"
          @click="toggleEditMode(false)"
          v-show="getEditMode"
          :aria-label="contentTitle ? `Save ${contentTitle} statement` : 'Save'"><i class="fa fa-save"/> Done</button>
        <CopyButton
          v-if="showCopyButton"
          btn-class="btn btn-default btn-xs"
          :copy-text="plainTextCurrentEdited"
          :success-text="copyTooltipText"
          :error-text="copyErrorText"
          :aria-label="contentTitle ? `Copy ${contentTitle} statement` : 'Copy'"><i class="fa fa-copy"/> Copy</CopyButton>
        <button
          class="btn btn-default btn-xs"
          @click="resetEditableText"
          v-show="!getEditMode && !noreset && isTextEdited"
          :aria-label="`Reset ${contentTitle}`"><i class="fa fa-undo"/> Reset</button>
        <slot name="translateButton" />
        <slot name="wikiTextFeedback" />
      </div>
      <div v-if="hasMultilingualResources && objectId">
        <ResourceLanguageDropdown :object-id="objectId" />
      </div>
    </div>
  </div>
</template>

<script>
import WikiText from '@/components/WikiText'
import WikiTextArea from '@/components/WikiTextArea'
import CopyButton from '@/components/CopyButton'
import { getSelection, getSelectedText, removeScreenReaderText } from '@/util/copypaste_helpers'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import { domElementFocus } from '@/util/dom_focus'
import ResourceLanguageDropdown from '@/components/ResourceLanguageDropdown'
import { mapGetters } from 'vuex'

export default {
  name: 'EditableWikiTextArea',
  components: {
    WikiText,
    WikiTextArea,
    CopyButton,
    ResourceLanguageDropdown
  },
  data() {
    return {
      editMode: false,
      originalText: '',
      copyErrorText: 'Error copying text. Use Ctrl+C instead.',
      currentEditedText: '', // This data is only used to provide the current text to CopyButton
      languages: []
    }
  },
  props: {
    value: {
      type: String,
      required: true
    },
    resetValue: {
      type: String,
      required: false,
      default: ''
    },
    strategies: {
      type: Object,
      default() {
        return {}
      }
    },
    resources: {
      type: Object,
      default() {
        return {}
      }
    },
    objectId: {
      type: String,
      default: ''
    },
    noreset: {
      type: Boolean,
      default: false
    },
    placeholderText: {
      type: String,
      default: ''
    },
    copyTooltipText: {
      type: String,
      default: 'Copied'
    },
    showCopyButton: {
      type: Boolean,
      default: true
    },
    showButtons: {
      type: Boolean,
      default: true
    },
    parentEditMode: {
      type: Boolean,
      default: undefined
    },
    textareaId: {
      type: String,
      default: undefined
    },
    contentTitle: {
      type: String,
      required: false,
      default: ''
    },
    evtData: {
      type: Object,
      required: false,
      default() {
        return {}
      }
    },
    hasResources: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    isTextEdited() {
      if (this.resetValue) {
        return this.value !== this.resetValue
      }
      return this.value !== this.originalText
    },
    plainTextCurrentEdited() {
      return wikiToPlainText(this.currentEditedText || this.value)
    },
    getEditMode() {
      if (this.parentEditMode === undefined || this.hasResources) {
        return this.editMode
      }
      return this.parentEditMode
    },
    ...mapGetters('goaldetail', ['hasNonEnglishLangById']),
    hasMultilingualResources() {
      if (!this.objectId) return false
      return this.hasNonEnglishLangById(this.objectId)
    },
    ...mapGetters({
      betaVisibility: 'visibility/betaVisibility'
    })
  },
  methods: {
    setOriginalText() {
      if (this.resetValue) {
        this.originalText = this.resetValue
      } else {
        this.originalText = this.value
      }
    },
    toggleEditIfNoSelection() {
      // activate edit mode if there is no text selection
      let selection = getSelection()
      if (selection && selection.anchorOffset === selection.focusOffset) {
        if (this.parentEditMode === undefined || this.hasResources) {
          this.toggleEditMode(true)
        } else {
          this.$emit('update:parentEditMode', true)
        }
        if (this.textareaId) {
          this.$nextTick(() => domElementFocus(`#${this.textareaId}`))
        }
      }
    },
    toggleEditMode(mode) {
      this.editMode = mode
      if (mode) {
        if (this.hasResources) {
          this.$sendEvent('Activated Edit Text', this.evtData)
        } else {
          this.$sendEvent('Activated Edit Text')
        }
        this.$emit('activatedEditText')
      } else {
        // using next tick so that the value prop has been updated by the emits
        this.$nextTick(() => this.$sendEvent('Deactivated Edit Text', {
            is_text_edited: this.isTextEdited,
            edited_text_text: this.value,
            ...(this.hasResources ? this.evtData || {} : {})
          })
        )
      }
    },
    updateValue(editedText, shouldParentIgnoreBlur) {
      this.$emit('input', editedText) // emit this event to update v-model
      this.$emit('textEdited', editedText) // emit this event as another hook to consuming components
      if (shouldParentIgnoreBlur) {
        return
      }
      if (this.parentEditMode === undefined || this.hasResources) {
        this.toggleEditMode(false)
      } else {
        this.$emit('update:parentEditMode', false)
      }
    },
    resetEditableText() {
      if (confirm('Are you sure you want to discard all changes?')) {
        this.currentEditedText = ''
        this.$emit('input', this.originalText)
        this.$emit('textReset', this.originalText)
        this.$sendEvent('Reset Edited Text')
      }
    },
    handleKeyboardCopy(event) {
      let selectedText = getSelectedText();
      event.clipboardData.setData('text/plain', removeScreenReaderText(selectedText));
      event.preventDefault();
    },
  },
  created() {
    // NOTE: this means that the component must be initalized when the value prop is already available/not empty string
    this.setOriginalText()
  },
  updated() {
    this.setOriginalText()
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';

.btn-toolbar {
  margin-top: 2px;
}
.rendered-text-container {
  position: relative;
  overflow: hidden;
  border: 1px solid transparent;
  padding: 1px;
  cursor: default;

  .rendered-text-container__hover {
    position: absolute;
    bottom: -29px;
    right: 0;
    background: rgba(0, 0, 0, 0.75);
    color: white;
    padding: 4px 8px;
    font-weight: normal;
    width: 220px;
    transition: transform 0.2s ease-in-out;
    pointer-events: none;
  }
  &:hover {
    border-color: darken(yellow, 8%);
    background: lighten(lightyellow, 5%);
    .rendered-text-container__hover {
      transform: translateY(-28px);
    }
  }
}
.separator {
  border-left: 1px solid #ADADAD;
  padding-left: 8px;
  line-height: 23px;
  font-size: 18px;
  float: left;
  margin-left: 8px;
}
.flex-container {
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 8px;
}
</style>
